'use client';

import Icon from '@ant-design/icons';
import { Modal } from 'antd';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import {
  usePathname,
  useRouter,
  useSearchParams,
  useSelectedLayoutSegments
} from 'next/navigation';
import { Suspense, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import AutoHide from '@/components/AutoHide';
import RightSvg from '@/components/Icon/svgs/right.svg';
import { CONTAINER_EVENT } from '@/components/chat-app/utils/utils';
import CreditRunOutModal from '@/components/credit-run-out-modal';
import type { ILayoutInfo } from '@/context/GeniusLayout';
import GeniusLayout from '@/context/GeniusLayout';
import HomeContext from '@/context/HomeContext';
import useDefer from '@/hooks/useDefer';
import useGenius from '@/hooks/useGenius';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import { useModel } from '@/hooks/useModel';
import useStorage from '@/hooks/useStorage';
import useSubscription from '@/hooks/useSubscription';
import { EMemoryType } from '@/service/note';
import useFlashbackStore from '@/store/useFlashbackStore';
import useNoteItemStore from '@/store/useNoteItemStore';
import useUserSettings from '@/store/useUserSettings';
import type { Type } from '@/types/chat';
import { EGlobalId } from '@/types/common';
import { EPlanAlias } from '@/types/subscription';
import type { ISourceCustomForChatTitleUpdate, TNotice } from '@/types/wsNotice';
import { ENoticeType } from '@/types/wsNotice';
import ModalChat from '@/ui/ModalChat';
import PAChat from '@/ui/PAChat';
import BringFriends from '@/ui/bring-friends';
import PhoneAdvertise from '@/ui/phone-advertise';
import Subscription from '@/ui/subscription';
import Visualization from '@/ui/visualization';
import { EVENT } from '@/utils/event';
import { ROUTER_PATH, prefetch } from '@/utils/router';

import ViewTabs from './LeftTabs';
import {
  LayoutSecondaryPanelEnum,
  PATabItems,
  ViewTabsEnum,
  ViewTabsEnumRouterMap,
  ViewTabsList
} from './constants';
import styles from './index.less';
import useActivity from './useActivity';

const ApiKeyManagement = dynamic(() => import('@/ui/api-key-management'), { ssr: false });

// Dynamic imports
const NoteAddMenu = dynamic(() => import('@/ui/PAHome/NoteAddMenu'), { ssr: false });
const MainContent = dynamic(() => import('./MainContent'), { ssr: false });

interface LayoutProps {
  children?: React.ReactNode;
  type: Type;
  uid?: string;
}

function LayoutComponent({ children, type }: LayoutProps): JSX.Element {
  const [tab] = useSelectedLayoutSegments() as [ViewTabsEnum];

  const [activeTab, setActiveTab] = useState<ViewTabsEnum>(tab || ViewTabsEnum.Home);
  const [isOpenNotify, setIsOpenNotify] = useState(false);

  const [chatStatus, setChatStatus] = useState<'hide' | 'normal' | 'fullScreen'>('hide');

  const router = useRouter();
  const pathName = usePathname();
  const updateChatNote = useNoteItemStore((state) => state.updateChatNote);
  const updateNote = useNoteItemStore((state) => state.updateNote);

  const [layoutSecondaryPanel, setLayoutSecondaryPanel] = useStorage('layoutSecondaryPanel');

  const currentPlanDetail = useSubscription((state) => state.currentPlanDetail);

  const { isLogin } = useModel('auth-model', (model) => ({
    isLogin: model.isLogin
  }));
  const [layoutSize] = useStorage('layoutSize');

  const { chatHomepageRoute, chatCircleRoute } = useContext(HomeContext);

  const geniusInfo = useGenius();

  const defer = useDefer();

  const [open, setOpen] = useState(false);

  // activity上报
  useActivity();

  const mindId = geniusInfo.shadowMindId;

  const mindName = geniusInfo.mindName;

  const hideTabs = !isLogin;

  const layoutInfo = useMemo<ILayoutInfo>(
    () => ({
      tabKey: activeTab,
      type: type,
      chatStatus: chatStatus
    }),
    [activeTab, chatStatus, type]
  );
  const searchParams = useSearchParams();
  const chosenBiog = useMemo(() => {
    return searchParams.get('chosenBiog');
  }, [searchParams]);

  useEffect(() => {
    if (chosenBiog && pathName === ROUTER_PATH.personalAI) {
      setLayoutSecondaryPanel(LayoutSecondaryPanelEnum.brainMap);
    }
  }, [chosenBiog, pathName]);

  const handleWebsiteAdd = () => {
    const content = localStorage.getItem('website_quiz_add_text');

    localStorage.removeItem('website_quiz_add_text');
    localStorage.removeItem('website_quiz_welcome_img');

    if (content && content != 'undefined') {
      setTimeout(() => {
        dispatchEvent(
          new CustomEvent(EVENT.AUTO_ADD_TEXT_NOTE, {
            detail: {
              type: EMemoryType.TEXT,
              content: content
            }
          })
        );
      }, 200);
    }
  };

  // 监听website转化
  useEffect(() => {
    if (!isLogin || !mindId) return;

    handleWebsiteAdd();

    setTimeout(() => {
      handleWebsiteAdd();
    }, 2000);
  }, [isLogin, mindId]);

  // 监听导入
  useEffect(() => {
    if (!isLogin || !mindId) return;

    if (sessionStorage.getItem('Integration_doing') == 'true') {
      setTimeout(() => dispatchEvent(new Event(EVENT.SHOW_IMPORT_MEMORIES)), 50);
    }
  }, [isLogin, mindId]);

  useEffect(() => {
    const _chatStatus = localStorage.getItem('chatStatus');

    if (_chatStatus) {
      setChatStatus(_chatStatus as 'hide' | 'normal' | 'fullScreen');
    } else {
      setChatStatus(layoutSize === LAYOUT_SIZE.LARGE ? 'normal' : 'hide');
    }
  }, [layoutSize]);

  useEffect(() => {
    localStorage.setItem('chatStatus', chatStatus);
  }, [chatStatus]);

  useEffect(() => {
    if (!tab) {
      setActiveTab(ViewTabsEnum.Home);
    } else {
      setActiveTab(tab);
    }
  }, [tab]);

  useEffect(() => {
    const handleOpenNotify = () => {
      setIsOpenNotify(true);
    };

    addEventListener(CONTAINER_EVENT.TRIAL_OVER, handleOpenNotify);

    return () => {
      removeEventListener(CONTAINER_EVENT.TRIAL_OVER, handleOpenNotify);
    };
  }, []);

  useEffect(() => {
    const handleChangeChatStatus = (e: Event) => {
      const event = e as CustomEvent;

      if (event.detail === 'hide') {
        dispatchEvent(new Event(EVENT.CLOSE_TASK_LIST));
      }

      setChatStatus((a) => {
        if (event.detail === 'fullScreen' && a === 'fullScreen') {
          return 'normal';
        }

        if (event.detail === 'normal' && a === 'normal') {
          return 'hide';
        }

        return event.detail;
      });
    };

    const handleChangeChatStatusTruly = (e: Event) => {
      const event = e as CustomEvent;

      if (event.detail === 'hide') {
        dispatchEvent(new Event(EVENT.CLOSE_TASK_LIST));
      }

      setChatStatus(event.detail);
    };

    addEventListener(EVENT.CHANGE_CHAT_STATUS_LAYOUT, handleChangeChatStatus);
    addEventListener(EVENT.CHANGE_CHAT_STATUS_LAYOUT_TRULY, handleChangeChatStatusTruly);

    return () => {
      removeEventListener(EVENT.CHANGE_CHAT_STATUS_LAYOUT, handleChangeChatStatus);
      removeEventListener(EVENT.CHANGE_CHAT_STATUS_LAYOUT_TRULY, handleChangeChatStatusTruly);
    };
  }, [tab]);

  const handleClickItem = (item: ViewTabsEnum) => {
    setActiveTab(item);

    if (type === 'pa') {
      router.push(ViewTabsEnumRouterMap[item]);
    }

    if (item === ViewTabsEnum.Friends) {
      dispatchEvent(new Event(EVENT.REFRESH_FRIEND_TAB));
    }

    dispatchEvent(new Event(EVENT.CLOSE_TASK_LIST));
    setChatStatus((_e) => {
      if (_e === 'hide') return 'hide';

      return 'normal';
    });
  };

  useEffect(() => {
    const handleSwitch = (e: Event) => {
      const event = e as CustomEvent;

      handleClickItem(event.detail as ViewTabsEnum);
    };

    const showModalChat = () => {
      setChatStatus('normal');
    };

    const hideModalChat = () => {
      setChatStatus('hide');
    };

    addEventListener(EVENT.SWITCH_VIEW_TABS, handleSwitch);
    addEventListener(EVENT.SHOW_MODAL_CHAT, showModalChat);
    addEventListener(EVENT.HIDE_MODAL_CHAT, hideModalChat);

    return () => {
      removeEventListener(EVENT.SWITCH_VIEW_TABS, handleSwitch);
      removeEventListener(EVENT.SHOW_MODAL_CHAT, showModalChat);
      removeEventListener(EVENT.HIDE_MODAL_CHAT, hideModalChat);
    };
  }, []);

  useEffect(() => {
    ViewTabsList.forEach((item) => {
      prefetch(router, ViewTabsEnumRouterMap[item]);
    });
  }, [router]);

  useEffect(() => {
    const cb = (ev: Event) => {
      if (ev instanceof CustomEvent) {
        const { detail } = ev;

        if (detail) {
          handleClickItem(detail);
        }
      }
    };

    addEventListener(EVENT.SELECT_LAYOUT_MENU_ITEM, cb);

    return () => {
      removeEventListener(EVENT.SELECT_LAYOUT_MENU_ITEM, cb);
    };
  }, []);

  useEffect(() => {
    const wsNotice = (event: Event) => {
      const e = event as CustomEvent;
      const data: TNotice = e.detail as TNotice;
      const sourceCustom = data.sourceCustom;

      if (data.sourceType === ENoticeType.chat) {
        const _sourceData = sourceCustom as ISourceCustomForChatTitleUpdate;

        if (_sourceData.type === 'titleUpdate') {
          updateChatNote(_sourceData.sessionId);
        }
      }

      if (data.sourceType === ENoticeType.note && data.sourceAction === 'edit') {
        updateNote(data.sourceId);
      }
    };

    addEventListener(EVENT.WS_NOTICE, wsNotice);

    return () => {
      removeEventListener(EVENT.WS_NOTICE, wsNotice);
    };
  }, []);

  useEffect(() => {
    const handleUpdate = (e: Event) => {
      const event = e as CustomEvent;

      updateChatNote(event.detail);
    };

    addEventListener(EVENT.UPDATE_CHAT_NOTE, handleUpdate);

    return () => {
      removeEventListener(EVENT.UPDATE_CHAT_NOTE, handleUpdate);
    };
  }, []);

  // 初始化数据
  useEffect(() => {
    if (!isLogin) return;

    useUserSettings.getState().initLanguage();
    useFlashbackStore.getState().fetchFlashback();
  }, [isLogin]);

  useEffect(() => {
    layoutSecondaryPanel && (setLayoutSecondaryPanel(layoutSecondaryPanel), setOpen(true));
  }, [layoutSecondaryPanel]);

  const layoutPanelContentMap = {
    [LayoutSecondaryPanelEnum.iosPhone]: <PhoneAdvertise />,
    [LayoutSecondaryPanelEnum.androidPhone]: <PhoneAdvertise type="android" />,
    [LayoutSecondaryPanelEnum.bring]: <BringFriends />,
    [LayoutSecondaryPanelEnum.subscription]: <Subscription />,
    [LayoutSecondaryPanelEnum.brainMap]: <Visualization />,
    [LayoutSecondaryPanelEnum.apiKey]: <ApiKeyManagement />,
    [LayoutSecondaryPanelEnum.NULL]: null
  };

  const layoutPanelContent =
    layoutPanelContentMap[layoutSecondaryPanel as LayoutSecondaryPanelEnum];

  // 在挂载和layoutSize变化时，将聊天块挂载到不同的地方
  const handleChatRef = useCallback(
    (node: any) => {
      if (node) {
        const sidebarContainer = document.querySelector('#sidebar-chat-container');

        const modalContainer = document.querySelector('#modal-chat-container');

        if (!sidebarContainer || !modalContainer || !node || layoutSize === undefined) return;

        if (layoutSize !== LAYOUT_SIZE.LARGE) {
          modalContainer.appendChild(node);
        } else {
          sidebarContainer.appendChild(node);
        }
      }
    },
    [layoutSize]
  );

  return (
    <div className={styles.geniusViewContainer}>
      <GeniusLayout layoutInfo={layoutInfo}>
        {/* 左边导航 + 中间主区域 */}
        <div className={styles.mainContainer}>
          {/* 导航栏 */}
          {defer(3) && (
            <ViewTabs
              className={styles.viewTabs}
              hideReturn={!!chosenBiog && pathName === ROUTER_PATH.personalAI}
              hideTabs={hideTabs}
              onClickTab={handleClickItem}
              tabList={PATabItems}
            />
          )}
          {/* 主区域浮层 */}
          {defer(2) && (
            <div>
              <AutoHide className={styles.floatViewContainer} classNoChild="w-0 h-0 hidden">
                {layoutPanelContent ? (
                  <Modal
                    afterClose={() => {
                      setLayoutSecondaryPanel(LayoutSecondaryPanelEnum.NULL);
                    }}
                    className={classNames(styles.modal, {
                      [styles.purpleBackground]: [LayoutSecondaryPanelEnum.brainMap].includes(
                        layoutSecondaryPanel
                      )
                    })}
                    closable={false}
                    destroyOnClose
                    footer={null}
                    onCancel={() => {
                      setOpen(false);
                    }}
                    open={open}
                    style={{ top: 0, padding: 0 }}
                    width={'100vw'}
                  >
                    <div className="absolute w-full h-[60px] top-0 left-0 z-[1] px-4 flex items-center">
                      <div
                        className={
                          'w-9 h-9 bg-[rgba(254,253,255,0.6)] rounded-[18px] p-1 cursor-pointer hover:bg-[rgba(254,253,255,0.8)] transition-all duration-200'
                        }
                        onClick={() => {
                          setOpen(false);
                        }}
                      >
                        <Icon
                          className="text-[28px] text-primary-black"
                          component={RightSvg}
                          rotate={180}
                        />
                      </div>
                    </div>
                    <div className="w-full">{layoutPanelContent}</div>
                    <div className={styles.gradient} />
                  </Modal>
                ) : null}
              </AutoHide>
              {/* ContentSecondaryPanel */}
              <AutoHide
                classHasChild={styles.secondaryFloatViewContainer}
                className="absolute"
                classNoChild="w-0 h-0 hidden"
                id={EGlobalId.CONTENT_SECONDARY_PANEL}
              />
            </div>
          )}
          {/* 主区域 */}
          {defer(1) && (
            <div
              className={classNames(styles.viewContainer, {
                [styles.fullScreenChat]: chatStatus === 'fullScreen',
                [styles.hideChat]:
                  chatStatus === 'hide' ||
                  (chatStatus === 'normal' && layoutSize !== LAYOUT_SIZE.LARGE)
              })}
              style={{
                display: !!chosenBiog && pathName === ROUTER_PATH.personalAI ? 'none' : ''
              }}
            >
              {/* 功能块 */}
              <Suspense>
                <div className={styles.contentContainer}>
                  {/* 强行拎出来，只能再包裹一层，要不就各个子界面自己做样式 */}
                  {/* 
                  主内容区
                  1. 处理内容区context
                  2. 添加全局loading、pending页
                  3. 还有滚动条问题，等待各个界面分别处理 
                */}
                  <MainContent mindId={mindId} mindName={mindName} type={type}>
                    {children}
                  </MainContent>
                  {mindId ? <NoteAddMenu /> : null}
                </div>
              </Suspense>

              {/* 聊天块 */}
              <Suspense>
                <div
                  className={classNames(
                    styles.chatContainer,
                    layoutSize && layoutSize !== LAYOUT_SIZE.LARGE ? '!hidden' : '',
                    {
                      [styles.friendHomepage]: chatHomepageRoute,
                      [styles.circleChat]: chatCircleRoute
                    }
                  )}
                  id="sidebar-chat-container"
                >
                  {/* 全局唯一chat实例 通过ref进行节点转移 */}
                  <PAChat
                    ref={handleChatRef}
                    className={layoutSize !== LAYOUT_SIZE.LARGE ? 'flex-1 !p-0' : ''}
                    inMobileDevice={layoutSize !== LAYOUT_SIZE.LARGE}
                  />
                </div>
              </Suspense>

              {/* Medium模式下的弹窗Chat */}
              <ModalChat />
            </div>
          )}
        </div>
      </GeniusLayout>
      {currentPlanDetail?.currentPlanAlias == EPlanAlias.FREE && (
        <CreditRunOutModal
          handleClose={() => {
            setIsOpenNotify(false);
          }}
          open={isOpenNotify}
        />
      )}
    </div>
  );
}

export default LayoutComponent;
